import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { deleteV1CompaniesCompanyIdDepartmentsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useRecordCompanySettings from '~hooks/useCompanySettings/useRecordCompanySettings';
import { Department } from '~hooks/useDepartment/models';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCursorPaginationQuery from '~queries/useCursorPaginationQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import DepartmentDialog from './DepartmentDialog';
import SuggestedRateDepartments from './SuggestedRateDepartments';

type DepartmentDialogState = {
  department?: Department;
  isOpen: boolean;
};

export const DepartmentManagement = observer(() => {
  const { companyAssetsStore, userStore, toasterStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);

  const { fetchDepartmentsPage } = useDepartment();
  const companySettings = useRecordCompanySettings(companyId);

  const [departmentFormDialogState, setDepartmentFormDialogState] =
    useState<DepartmentDialogState>({
      isOpen: false,
    });

  const [deleteDepartmentDialogState, setDeleteDepartmentDialogState] = useState<
    DepartmentDialogState & { isSubmitting: boolean }
  >({ isOpen: false, isSubmitting: false });

  const {
    goToPage: goToDepartmentsPage,
    isFetching,
    pagination: departmentsPagination,
    reloadCurrentPage: reloadDepartmentsPage,
    rows,
    startOver: startDepartmentsOver,
  } = useCursorPaginationQuery<Department>({
    fetchPage: ({ cursor, link, signal }) =>
      fetchDepartmentsPage({
        companyId,
        cursor,
        limit: MAX_API_PAGE_SIZE,
        link,
        signal,
      }),
    limit: MAX_API_PAGE_SIZE,
    queryKey: ['departments', companyId],
  });

  const userPermissions = userStore.getPermissions();

  const handleCreateNewDepartmentClick = useCallback(() => {
    setDepartmentFormDialogState({ isOpen: true });
  }, []);

  const handleDeleteDepartmentClick = useCallback((department: Department) => {
    setDeleteDepartmentDialogState({ isOpen: true, isSubmitting: false, department });
  }, []);

  const handleCloseDepartmentFormDialog = useCallback(() => {
    setDepartmentFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const isEditingDepartment = Boolean(departmentFormDialogState.department);
  const handleDepartmentSaved = useCallback(() => {
    if (isEditingDepartment) {
      void reloadDepartmentsPage();
    } else {
      void startDepartmentsOver();
    }
  }, [isEditingDepartment, reloadDepartmentsPage, startDepartmentsOver]);

  const handleCloseDeleteDepartmentDialog = useCallback(() => {
    setDeleteDepartmentDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const deleteDepartment = useCallback(async () => {
    const departmentId = deleteDepartmentDialogState.department?.id;

    if (!departmentId) {
      return;
    }

    try {
      setDeleteDepartmentDialogState((state) => ({ ...state, isSubmitting: true }));

      await deleteV1CompaniesCompanyIdDepartmentsId({
        path: {
          'company-id': companyId,
          id: departmentId,
        },
      });

      // department pickers elsewhere read this list, and the grid reload doesn't touch it
      companyAssetsStore.deleteDepartment(departmentId);
      void reloadDepartmentsPage();
      toasterStore.push(
        alert(t('administration.department.deleted'), AlertTypes.success),
      );
      setDeleteDepartmentDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setDeleteDepartmentDialogState((state) => ({ ...state, isSubmitting: false }));
      connection.handleRequestError(
        error,
        t('error_messages.departments.failed_to_delete'),
      );
    }
  }, [companyId, deleteDepartmentDialogState.department?.id, reloadDepartmentsPage]);

  const columns = useMemo<Array<DataTableColumn<Department>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (department) => department.name,
      },
      {
        field: 'companyName',
        headerName: t('form_fields.company'),
        valueGetter: (department) => department.companyName,
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (department) => (
          <Tooltip
            title={`${t('common.last_update')}: ${dateFormat(department.updatedAt)}`}
          >
            <span>{dateFormat(department.createdAt)}</span>
          </Tooltip>
        ),
      },
      actionsColumn((department) => {
        const options = userPermissions.canDeleteDepartment
          ? [
              {
                title: t('actions.delete'),
                icon: <Delete />,
                callBack: () => handleDeleteDepartmentClick(department),
              },
            ]
          : [];

        return (
          <>
            {userPermissions.canEditDepartment && (
              <IconButton
                size="small"
                // Accessible name for this icon-only edit button; also the
                // prod-safe e2e anchor (MUI's dev-only `EditIcon` testid is
                // stripped in staging builds).
                aria-label={t('actions.edit')}
                onClick={() => {
                  setDepartmentFormDialogState({ isOpen: true, department });
                }}
              >
                <Edit />
              </IconButton>
            )}
            {options && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [handleDeleteDepartmentClick, userPermissions],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('administration.department.title')}
        description={t('settings.page_descriptions.departments')}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <SuggestedRateDepartments
          companyId={companyId}
          isRateByDepartmentNameRuleOn={Boolean(
            companySettings?.isEnabled('require_rates_by_department_name'),
          )}
          onCreated={() => void startDepartmentsOver()}
        />
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateDepartment}
          onClick={handleCreateNewDepartmentClick}
          startIcon={<Add />}
        >
          {t('administration.department.create')}
        </SmallButton>
      </Box>
      <Box data-test-id={'departments-data-grid'}>
        <DataTable
          id="departments-data-grid"
          columns={columns}
          rows={rows}
          loading={isFetching || deleteDepartmentDialogState.isSubmitting}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetching || deleteDepartmentDialogState.isSubmitting}
              onNavigate={(link) => void goToDepartmentsPage(link)}
              pagination={departmentsPagination}
            />
          }
        />
      </Box>
      <DepartmentDialog
        department={departmentFormDialogState.department}
        isOpen={departmentFormDialogState.isOpen}
        onClose={handleCloseDepartmentFormDialog}
        onSuccess={handleDepartmentSaved}
      />
      <ConfirmationDialog
        isBusy={deleteDepartmentDialogState.isSubmitting}
        isOpen={deleteDepartmentDialogState.isOpen}
        onClose={handleCloseDeleteDepartmentDialog}
        onConfirm={deleteDepartment}
        title={t('administration.department.delete')}
      >
        <Typography>{t('administration.department.delete_description')}</Typography>
      </ConfirmationDialog>
    </>
  );
});
